import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Btn } from "@/components/site/kit";
import { api, errorMessage } from "@/lib/api";

const useForm = (initial, endpoint, check) => {
  const [f, setF] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [ref, setRef] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const errs = check(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, f);
      setRef(data.reference);
      toast.success("Request submitted successfully");
      setF(initial);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  return { f, set, errors, loading, ref, submit };
};

const Field = ({ id, label, error, as = "input", testId, ...props }) => {
  const El = as;
  return (
    <div>
      <label htmlFor={id} className="label-base">{label}</label>
      <El id={id} className={`input-base ${as === "textarea" ? "resize-none" : ""}`} data-testid={testId} {...props} />
      {error && <p className="mt-1 text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
};

const Success = ({ refId, testId, text }) => (
  <div className="flex flex-col items-center py-8 text-center" data-testid={testId}>
    <CheckCircle2 className="h-12 w-12 text-success" />
    <h3 className="mt-3 text-lg font-bold">Request received</h3>
    <p className="mt-1 text-sm text-body">Reference ID: <b className="text-navy" data-testid={`${testId}-ref`}>{refId}</b></p>
    <p className="mt-1 max-w-sm text-sm text-body">{text}</p>
  </div>
);

const honeypot = (set, v) => <input type="text" tabIndex={-1} autoComplete="off" value={v} onChange={set("website")} className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" />;

export const DeletionForm = () => {
  const { f, set, errors, loading, ref, submit } = useForm({ name: "", contact: "", message: "", website: "" }, "/data-deletion-requests", (x) => {
    const e = {};
    if (x.name.trim().length < 2) e.name = "Enter your name";
    if (x.contact.trim().length < 5) e.contact = "Enter the email or phone you used with us";
    return e;
  });
  if (ref) return <Success refId={ref} testId="deletion-success" text="We will confirm within 48 hours and complete deletion within 30 days." />;
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="deletion-form">
      <Field id="dd-name" label="Full name *" value={f.name} onChange={set("name")} error={errors.name} testId="deletion-name-input" />
      <Field id="dd-contact" label="Email or phone *" value={f.contact} onChange={set("contact")} error={errors.contact} testId="deletion-contact-input" />
      <Field id="dd-msg" as="textarea" rows={4} label="Message" value={f.message} onChange={set("message")} placeholder="Anything that helps us locate your data" testId="deletion-message-input" />
      {honeypot(set, f.website)}
      <Btn type="submit" disabled={loading} testId="deletion-submit-btn" arrow={!loading}>{loading && <Loader2 className="h-4 w-4 animate-spin" />}Submit Deletion Request</Btn>
    </form>
  );
};

export const GrievanceForm = () => {
  const { f, set, errors, loading, ref, submit } = useForm({ name: "", email: "", phone: "", order_id: "", subject: "", description: "", website: "" }, "/grievances", (x) => {
    const e = {};
    if (x.name.trim().length < 2) e.name = "Enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x.email)) e.email = "Enter a valid email";
    if (x.subject.trim().length < 3) e.subject = "Enter a subject";
    if (x.description.trim().length < 10) e.description = "Please describe your complaint (min 10 characters)";
    return e;
  });
  if (ref) return <Success refId={ref} testId="grievance-success" text="We will acknowledge within 48 hours and resolve within 30 days." />;
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="grievance-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="gr-name" label="Full name *" value={f.name} onChange={set("name")} error={errors.name} testId="grievance-name-input" />
        <Field id="gr-email" type="email" label="Email *" value={f.email} onChange={set("email")} error={errors.email} testId="grievance-email-input" />
        <Field id="gr-phone" type="tel" label="Phone" value={f.phone} onChange={set("phone")} testId="grievance-phone-input" />
        <Field id="gr-order" label="Order ID (if any)" value={f.order_id} onChange={set("order_id")} testId="grievance-order-input" />
      </div>
      <Field id="gr-subject" label="Subject *" value={f.subject} onChange={set("subject")} error={errors.subject} testId="grievance-subject-input" />
      <Field id="gr-desc" as="textarea" rows={5} label="Complaint details *" value={f.description} onChange={set("description")} error={errors.description} testId="grievance-description-input" />
      {honeypot(set, f.website)}
      <Btn type="submit" disabled={loading} testId="grievance-submit-btn" arrow={!loading}>{loading && <Loader2 className="h-4 w-4 animate-spin" />}Submit Complaint</Btn>
    </form>
  );
};
